import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import {
  matchPending,
  type ForwardTarget,
  type PendingForward,
  type ReturnTo,
} from './forwarding'

/**
 * In-memory shared state for the "Copilot message forwarding" round-trip.
 *
 * Lives in React state only — it survives client-side navigation (the provider
 * stays mounted across routes) but is cleared on a full page refresh, so a
 * reload can never re-inject a stale prefill.
 *
 *   forwardToCopilot(...)  — agent forwards a chat/ticket message INTO Copilot.
 *   forwardToChat(...)     — agent forwards a Copilot answer BACK to a chat/ticket.
 *   consume(matcher)       — a composer claims its own prefill, once.
 *   returnTo / clearReturnTo — the source chat/ticket for the round-trip back.
 */
interface ForwardingContextValue {
  pending: PendingForward | null
  returnTo: ReturnTo | null
  forwardToCopilot: (args: {
    text: string
    sessionId: string | number | 'new'
    returnTo?: ReturnTo | null
  }) => void
  forwardToChat: (args: { text: string; target: ForwardTarget }) => void
  consume: (matcher: (target: ForwardTarget) => boolean) => string | null
  clearReturnTo: () => void
}

const ForwardingContext = createContext<ForwardingContextValue | undefined>(undefined)

export const ForwardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingForward | null>(null)
  const [returnTo, setReturnTo] = useState<ReturnTo | null>(null)

  // Mirror pending in a ref so consume() reads the latest value synchronously
  // even if two composers mount in the same tick.
  const pendingRef = useRef<PendingForward | null>(null)
  pendingRef.current = pending

  const forwardToCopilot = useCallback(
    ({
      text,
      sessionId,
      returnTo: rt,
    }: {
      text: string
      sessionId: string | number | 'new'
      returnTo?: ReturnTo | null
    }) => {
      setPending({ text, target: { kind: 'copilot', id: sessionId } })
      setReturnTo(rt ?? null)
    },
    []
  )

  const forwardToChat = useCallback(
    ({ text, target }: { text: string; target: ForwardTarget }) => {
      setPending({ text, target })
    },
    []
  )

  const consume = useCallback((matcher: (target: ForwardTarget) => boolean): string | null => {
    const text = matchPending(pendingRef.current, matcher)
    if (text !== null) {
      pendingRef.current = null
      setPending(null)
    }
    return text
  }, [])

  const clearReturnTo = useCallback(() => setReturnTo(null), [])

  const value: ForwardingContextValue = {
    pending,
    returnTo,
    forwardToCopilot,
    forwardToChat,
    consume,
    clearReturnTo,
  }

  return <ForwardingContext.Provider value={value}>{children}</ForwardingContext.Provider>
}

export const useForwarding = (): ForwardingContextValue => {
  const ctx = useContext(ForwardingContext)
  if (!ctx) throw new Error('useForwarding must be used within a ForwardingProvider')
  return ctx
}
